import React from 'react';
import Head from 'next/head';
import VerticalContent from '../../../resources/js/components/VerticalContent/VerticalContent';
import ArticleTitle from '../../../resources/js/components/ArticleTitle/ArticleTitle';
import ArticleCategoryViewModel from '../../../resources/js/typings/viewModels/articleCategoryViewModel';
import PageOfArticlePreviewsViewModel from '../../../resources/js/typings/viewModels/pageOfArticlePreviewsViewModel';
import convertImageUrlToOptimizedImageUrl from '../../helpers/convertImageUrlToOptimizedImageUrl';
import useGame from '../../../resources/js/contexts/useGame';
import styles from './ArticleCategory.module.scss';
import LinkButton from '../../../resources/js/components/LinkButton/LinkButton';
import Link from 'next/link';
import Navigation from '../Navigation/Navigation';
import CmsNavigationViewModel from '../../../resources/js/typings/viewModels/cmsNavigationViewModel';
import Content from '../../../resources/js/components/Content/Content';
import ArticlePreviewList from '../../../resources/js/components/ArticlePreviewList/ArticlePreviewList';
import ArticlePreview from '../ArticlePreview/ArticlePreview';
import useGameSlug from '../../hooks/useGameSlug';

type Props = {
  pageNumber: number;
  articleCategory: ArticleCategoryViewModel;
  navigation: CmsNavigationViewModel;
  pageOfArticlePreviews: PageOfArticlePreviewsViewModel;
  gameLabel: string;
};

function ArticleCategory({
  pageNumber,
  articleCategory,
  navigation,
  pageOfArticlePreviews,
  gameLabel,
}: Props): JSX.Element | null {
  const gameSlug = useGameSlug();
  const game = useGame();
  if (!game) return null;

  let title = `${articleCategory.name} Articles`;
  if (pageNumber > 1) title += ` (Page ${pageNumber})`;

  const description = `${
    pageNumber === 1 ? 'The latest' : `Page ${pageNumber} of`
  } ${articleCategory.name.toLowerCase()} articles from ${game?.siteTitle}.`;

  const featuredImageUrl = pageOfArticlePreviews.articlePreviews[0].imageUrl;

  const previousPageUrl =
    pageNumber > 1
      ? pageNumber === 2
        ? `/${gameSlug}/articles/${articleCategory.slug}`
        : `/${gameSlug}/articles/${articleCategory.slug}/page/${pageNumber - 1}`
      : undefined;
  const nextPageUrl = pageOfArticlePreviews.isLastPage
    ? undefined
    : `/${gameSlug}/articles/${articleCategory.slug}/page/${pageNumber + 1}`;

  return (
    <>
      <Head>
        <title>{`${title} | Archon (${gameLabel})`}</title>
        <meta property='og:title' key='og:title' content={title} />
        <meta name='description' key='description' content={description} />
        <meta
          property='og:description'
          key='og:description'
          content={description}
        />
        {articleCategory.preventIndexing && (
          <>
            <meta name='robots' content='noindex' />
            <meta name='googlebot' content='noindex' />
          </>
        )}
        <meta property='og:type' key='og:type' content='website' />
        {featuredImageUrl && (
          <>
            <meta
              key='og:image'
              property='og:image'
              content={featuredImageUrl}
            />
            <meta property='twitter:image' content={featuredImageUrl} />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={title} />
          </>
        )}
      </Head>
      <article>
        <VerticalContent>
          <ArticleTitle
            title={title}
            backgroundImageUrl={convertImageUrlToOptimizedImageUrl(
              articleCategory.titleImageUrl,
              384,
              50
            )}
            isSlim
          />
          <Content width={'medium'}>
            <Navigation {...navigation} />
            <section className={styles.content}>
              <ArticlePreviewList
                ArticlePreview={ArticlePreview}
                articlePreviews={pageOfArticlePreviews.articlePreviews}
              />
              <div className={styles.pagination}>
                <LinkButton
                  href={previousPageUrl}
                  Wrapper={
                    previousPageUrl
                      ? ({ children }) => (
                          <Link href={previousPageUrl}>{children}</Link>
                        )
                      : undefined
                  }
                >
                  Previous
                </LinkButton>
                <LinkButton
                  href={nextPageUrl}
                  Wrapper={
                    nextPageUrl
                      ? ({ children }) => (
                          <Link href={nextPageUrl}>{children}</Link>
                        )
                      : undefined
                  }
                >
                  Next
                </LinkButton>
              </div>
            </section>
          </Content>
        </VerticalContent>
      </article>
    </>
  );
}

export default ArticleCategory;
